import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  TimeScale,
} from "chart.js";
import { Campaign } from "../../data/campaignData";
import {
  StatisticsCards,
  MostEffectiveCampaign,
  Top5Campaigns,
  BarChart,
  PieChart,
  LineChart,
  ProductChart,
  CodeStatistics,
  RedemptionLineChart,
  CombinedAccessViewsChart,
} from "./charts";
import { apiService, WinPrize, Campaign as ApiCampaign } from "../../services";

// Đăng ký các components cần thiết cho Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  TimeScale
);

interface CampaignChartsProps {
  campaigns: Campaign[];
}

interface Redemption {
  id: string;
  date: string;
  fullName: string;
  phone: string;
  codes: string[];
  campaign: string;
  status: "completed" | "pending" | "cancelled";
  totalValue: number;
  winCount: number;
}

const CampaignCharts: React.FC<CampaignChartsProps> = ({ campaigns }) => {
  const filteredCampaigns = campaigns;

  // State for redemption data
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [apiCampaigns, setApiCampaigns] = useState<ApiCampaign[]>([]);
  const [selectedRedemptionCampaign, setSelectedRedemptionCampaign] =
    useState<string>("all");
  const [loading, setLoading] = useState(false);

  // Transform API data to component format
  const transformWinPrizeToRedemption = (
    winPrize: WinPrize,
    campaignsList: ApiCampaign[]
  ): Redemption => {
    const campaign = campaignsList.find(
      (c) => c._id && c._id === winPrize.campaign_id
    );
    const campaignName = campaign
      ? campaign.name
      : "Chiến dịch không còn tồn tại";

    const winAny = winPrize as unknown as {
      code?: string;
      codes?: string[];
      num_win?: number;
    };
    const codeList = Array.isArray(winAny.codes)
      ? winAny.codes
      : typeof winAny.code === "string"
      ? winAny.code
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    return {
      id: winPrize._id,
      date: winPrize.winning_day,
      fullName: winPrize.prize_winner,
      phone: winPrize.phone,
      codes: codeList,
      campaign: campaignName,
      status: "completed",
      totalValue: 0,
      winCount:
        typeof winAny.num_win === "number"
          ? winAny.num_win
          : codeList.length || 1,
    };
  };

  // Fetch redemption data
  useEffect(() => {
    const fetchRedemptionData = async () => {
      try {
        setLoading(true);
        const [campaignsResponse, winPrizesResponse] = await Promise.all([
          apiService.getCampaigns(),
          apiService.getWinPrizes(),
        ]);

        if (campaignsResponse.success && campaignsResponse.data) {
          setApiCampaigns(campaignsResponse.data);
        }

        if (winPrizesResponse.success && winPrizesResponse.data) {
          const transformedRedemptions = winPrizesResponse.data.map(
            (winPrize) =>
              transformWinPrizeToRedemption(
                winPrize,
                campaignsResponse.data || []
              )
          );
          setRedemptions(transformedRedemptions);
        }
      } catch (error) {
        console.error("Error fetching redemption data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRedemptionData();
  }, []);

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 min-h-screen">
      {/* Header với tiêu đề */}
      <div className="mb-8 text-left">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">
          Dashboard Thống kê Chiến dịch
        </h1>
        <p className="text-gray-600 dark:text-gray-300 text-base">
          Theo dõi hiệu suất và phân tích chi tiết các chiến dịch marketing
        </p>
      </div>

      {/* Thống kê tổng quan */}
      <StatisticsCards campaigns={campaigns} />

      {/* Biểu đồ đường cho dữ liệu đổi quà */}
      <div className="">
        <RedemptionLineChart
          redemptions={redemptions}
          campaigns={apiCampaigns as any}
          selectedCampaign={selectedRedemptionCampaign}
          onCampaignChange={setSelectedRedemptionCampaign}
        />
      </div>

      {/* Biểu đồ so sánh lượng truy cập link và lượt xem video 3D */}
      <div className="mt-6">
        <CombinedAccessViewsChart />
      </div>

      {/* Chiến dịch hiệu quả nhất */}
      {/* <MostEffectiveCampaign /> */}

      {/* Top 5 Chiến dịch Hiệu quả */}
      {/* <Top5Campaigns /> */}

      {/* Biểu đồ chính */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8"> */}
      {/* Biểu đồ cột */}
      {/* <BarChart filteredCampaigns={filteredCampaigns} /> */}

      {/* Biểu đồ tròn */}
      {/* <PieChart campaigns={campaigns} /> */}
      {/* </div> */}

      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8"> */}
      {/* Biểu đồ đường */}
      {/* <LineChart filteredCampaigns={filteredCampaigns} /> */}

      {/* Biểu đồ cột ngang cho sản phẩm */}
      {/* <ProductChart filteredCampaigns={filteredCampaigns} /> */}
      {/* </div> */}

      {/* Thống kê chi tiết mã */}
      {/* <CodeStatistics campaigns={campaigns} /> */}
    </div>
  );
};

export default CampaignCharts;
